import React, { useEffect, useState } from "react";
import { Box, Divider, Typography, useTheme, alpha } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import BoltIcon from "@mui/icons-material/Bolt";

import { useGetMembersQuery } from "components/Slices/circleApiSlice";
import GroupData from "./sections/GroupData";
import GroupHeader from "./sections/GroupHeader";
import PendingAction from "./sections/PendingAction";

import AddUser from "./AddUser";

import { useUserStatusMutation } from "../../../Slices/usersApiSlice";
import UserStatusModal from "components/Circle/shared/UserStatusModal";
import PendingMembersCard from "./sections/PendingMembersCard";
import { MoneyLoading } from "components/routers/MoneyLoading";
import PendingRequestsSection from "components/Circle/shared/PendingRequestsSection";

function PendingPage(props) {
  const { groupInfo } = props;
  const theme = useTheme();

  const [group] = useState(groupInfo);
  const [userStatus] = useUserStatusMutation();
  const [status, setStatus] = useState();
  const [canAddSelf, setCanAddSelf] = useState();
  const [requestType, setRequestType] = useState();
  const [open, setOpen] = useState(false);

  const { data: members, isLoading } = useGetMembersQuery(group.id, {
    refetchOnMountOrArgChange: true,
  });
  const [newUser, setNewUser] = useState({});

  useEffect(() => {
    const myStatus = async () => {
      try {
        const res = await userStatus({ groupId: group.id }).unwrap();
        setStatus(res.status);
        setCanAddSelf(res.canAddSelf);
        setRequestType(res.requestType);
      } catch (err) {
        console.log({ err });
      }
    };
    myStatus();
  }, [newUser]);

  if (isLoading) {
    return <MoneyLoading />;
  }

  const sectionCard = {
    borderRadius: 4,
    bgcolor: "background.paper",
    overflow: "hidden",
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ── Left column ── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Circle info card */}
          <Box
            sx={{
              ...sectionCard,
              boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 6px 24px rgba(16,24,64,0.1)",
            }}
          >
            <Box sx={{ height: 4, bgcolor: "warning.main" }} />
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <GroupHeader group={group} />
              <Divider sx={{ my: 3 }} />
              <GroupData group={group} memberCount={members?.length || 0} />
            </Box>
          </Box>

          {/* Pending requests (admin only) */}
          <PendingRequestsSection groupId={group.id} isAdmin={group.isAdmin} />

          {/* Participants */}
          <Box
            sx={{
              ...sectionCard,
              overflow: "visible",
              boxShadow: "0 1px 3px rgba(16,24,64,0.05), 0 4px 16px rgba(16,24,64,0.08)",
            }}
          >
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {/* Section header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                </Box>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "text.primary" }}>
                  Participants
                </Typography>
                {members && (
                  <Box
                    sx={{
                      ml: "auto",
                      px: 1.25,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "primary.main" }}>
                      {members.length} joined
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <PendingMembersCard
                  setOpen={setOpen}
                  startDate={group.nextPayout}
                  members={members}
                  adminId={group.adminId}
                />
                {(group.isAdmin || !group.isPrivate) && (
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <AddUser
                      currentMembers={members}
                      group={group}
                      newUser={() => setNewUser}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

        </Box>{/* end left column */}

        {/* ── Right column: Action ── */}
        <Box sx={{ position: "sticky", top: 24 }}>
          <Box
            sx={{
              ...sectionCard,
              boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 6px 24px rgba(16,24,64,0.1)",
            }}
          >
            {/* Accent bar */}
            <Box sx={{ height: 4, bgcolor: "primary.main" }} />

            <Box sx={{ p: 3 }}>
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BoltIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
                    Actions
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                    {group.isAdmin ? "Manage this circle" : "Your membership status"}
                  </Typography>
                </Box>
              </Box>
              <PendingAction
                group={group}
                status={status}
                canAddSelf={canAddSelf}
                requestType={requestType}
                members={members}
              />
            </Box>
          </Box>
        </Box>

      </Box>

      <UserStatusModal
        group={group}
        open={open}
        onClose={() => setOpen(false)}
        members={members}
      />
    </Box>
  );
}

export default PendingPage;
